import * as React from "react";
import {
  Listbox as ShadcnListbox,
  ListboxItem as ShadcnListboxItem,
} from "../listbox";
import { cn } from "@/lib/utils";

// Context for managing dropdown open/close state
interface ListboxDropdownContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
}

const ListboxDropdownContext = React.createContext<
  ListboxDropdownContextValue | undefined
>(undefined);

const useListboxDropdown = (): ListboxDropdownContextValue => {
  const context = React.useContext(ListboxDropdownContext);
  if (!context) {
    throw new Error(
      "useListboxDropdown must be used within ListboxDropdownProvider"
    );
  }
  return context;
};

// ListboxItem with Hero UI compatible props
export interface ListboxItemProps extends Omit<
  React.LiHTMLAttributes<HTMLLIElement>,
  "value"
> {
  key?: string;
  textValue?: string;
  value?: string | number | object;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  shouldHighlightOnFocus?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
}

const ListboxItem = React.forwardRef<HTMLLIElement, ListboxItemProps>(
  (
    {
      className,
      children,
      value,
      textValue,
      startContent,
      endContent,
      shouldHighlightOnFocus = true,
      isSelected,
      isDisabled,
      ...props
    },
    ref
  ) => {
    // Convert value to string for data attribute
    const valueString = React.useMemo(() => {
      if (value === undefined || value === null) return undefined;
      if (typeof value === "string" || typeof value === "number") {
        return String(value);
      }
      // For objects, try to get a unique identifier or stringify
      if (typeof value === "object") {
        const obj = value as any;
        // Try common identifier fields
        if (obj.id) return String(obj.id);
        if (obj.contract && obj.tokenId)
          return `${obj.contract}-${obj.tokenId}`;
        if (obj.contract) return String(obj.contract);
        if (obj.tokenId) return String(obj.tokenId);
        // Fallback to JSON string (but this might be too long)
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }
      return String(value);
    }, [value]);

    return (
      <ShadcnListboxItem
        ref={ref}
        className={cn(
          "w-full",
          className,
          isSelected && "bg-accent",
          !shouldHighlightOnFocus && "focus:bg-transparent",
          // Override default opacity when disabled if endContent exists
          isDisabled && endContent && "opacity-100!",
          isDisabled && "cursor-not-allowed"
        )}
        {...(isSelected && { "data-selected": "true" })}
        {...(isDisabled && { "data-disabled": "true" })}
        {...(valueString && { "data-value": valueString })}
        {...props}
      >
        <div
          className={cn(
            "flex items-center justify-between w-full",
            isDisabled && "pointer-events-none"
          )}
        >
          {startContent && (
            <div
              className={cn(
                "mr-2 flex items-center",
                isDisabled && "opacity-50"
              )}
            >
              {startContent}
            </div>
          )}
          <div className={cn("flex-1", isDisabled && "opacity-50")}>
            {children}
          </div>
          {endContent && (
            <div className="ml-2 flex items-center listbox-end-content">
              {endContent}
            </div>
          )}
        </div>
      </ShadcnListboxItem>
    );
  }
);
ListboxItem.displayName = "ListboxItem";

// Listbox with Hero UI compatible props
export interface ListboxProps<T = any> extends Omit<
  React.HTMLAttributes<HTMLUListElement>,
  "children" | "onChange"
> {
  items?: T[];
  selectionMode?: "single" | "multiple" | "none";
  selectedKeys?: string[] | Set<string> | "all";
  onSelectionChange?: (keys: string[] | Set<string>) => void;
  value?: T | T[];
  onChange?: (value: T | T[] | React.ChangeEvent<HTMLUListElement>) => void;
  variant?: "flat" | "bordered" | "faded" | "underlined";
  itemClasses?: {
    base?: string;
  };
  hideSelectedIcon?: boolean;
  emptyContent?: React.ReactNode;
  disabledKeys?: string[] | Set<string> | ((item: T) => boolean);
  classNames?: {
    base?: string;
    list?: string;
    emptyContent?: string;
  };
  label?: string;
  disabled?: boolean;
  disallowEmptySelection?: boolean;
  children?: ((item: T) => React.ReactNode) | React.ReactNode;
}

const Listbox = React.forwardRef<HTMLUListElement, ListboxProps<any>>(
  (
    {
      items,
      selectionMode = "none",
      selectedKeys: controlledSelectedKeys,
      onSelectionChange,
      value,
      onChange,
      variant = "flat",
      itemClasses,
      hideSelectedIcon = false,
      emptyContent,
      disabledKeys,
      classNames,
      label,
      disabled,
      disallowEmptySelection = false,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const context = React.useContext(ListboxDropdownContext);
    const [internalSelectedKeys, setInternalSelectedKeys] = React.useState<
      Set<string>
    >(new Set());
    const isControlled = controlledSelectedKeys !== undefined;
    const selectedKeysSet = React.useMemo(() => {
      if (isControlled) {
        if (controlledSelectedKeys === "all") {
          return new Set(
            items?.map((item: any) => item?.id ?? String(item)) ?? []
          );
        }
        if (Array.isArray(controlledSelectedKeys)) {
          return new Set(controlledSelectedKeys);
        }
        if (controlledSelectedKeys instanceof Set) {
          return controlledSelectedKeys;
        }
        return new Set();
      }
      return internalSelectedKeys;
    }, [controlledSelectedKeys, internalSelectedKeys, items, isControlled]);

    const isItemDisabledByKey = React.useCallback(
      (itemKey: string): boolean => {
        if (!disabledKeys) return false;
        if (typeof disabledKeys === "function") {
          const item = items?.find(
            (item: any) => (item?.id ?? String(item)) === itemKey
          );
          return item ? disabledKeys(item) : false;
        }
        if (Array.isArray(disabledKeys)) {
          return disabledKeys.includes(itemKey);
        }
        if (disabledKeys instanceof Set) {
          return disabledKeys.has(itemKey);
        }
        return false;
      },
      [disabledKeys, items]
    );

    const handleItemClick = React.useCallback(
      (itemKey: string) => {
        if (selectionMode === "none") return;

        const isDisabled = isItemDisabledByKey(itemKey);
        if (isDisabled) return;

        const newSelectedKeys = new Set(selectedKeysSet);
        const isCurrentlySelected = newSelectedKeys.has(itemKey);

        if (selectionMode === "single") {
          // If disallowEmptySelection is true and item is already selected, don't allow deselecting
          if (disallowEmptySelection && isCurrentlySelected) {
            return;
          }
          newSelectedKeys.clear();
          newSelectedKeys.add(itemKey);
        } else if (selectionMode === "multiple") {
          if (newSelectedKeys.has(itemKey)) {
            // If disallowEmptySelection is true, don't allow deselecting
            if (disallowEmptySelection) {
              return;
            }
            newSelectedKeys.delete(itemKey);
          } else {
            newSelectedKeys.add(itemKey);
          }
        }

        if (!isControlled) {
          setInternalSelectedKeys(newSelectedKeys);
        }

        if (onSelectionChange) {
          if (selectionMode === "single") {
            onSelectionChange(Array.from(newSelectedKeys));
          } else {
            onSelectionChange(newSelectedKeys);
          }
        }
      },
      [
        selectionMode,
        selectedKeysSet,
        isControlled,
        onSelectionChange,
        isItemDisabledByKey,
        disallowEmptySelection,
      ]
    );

    const variantClasses = {
      flat: "border-0",
      bordered: "border-1.5",
      faded: "border border-gray-200",
      underlined: "border-b-2 border-t-0 border-l-0 border-r-0 rounded-none",
    };

    const isItemDisabled = React.useCallback(
      (item: any): boolean => {
        if (!disabledKeys) return false;
        const itemKey = item?.id ?? String(item);
        if (typeof disabledKeys === "function") {
          return disabledKeys(item);
        }
        if (Array.isArray(disabledKeys)) {
          return disabledKeys.includes(itemKey);
        }
        if (disabledKeys instanceof Set) {
          return disabledKeys.has(itemKey);
        }
        return false;
      },
      [disabledKeys]
    );

    const renderItems = (): React.ReactNode => {
      if (!items || items.length === 0) {
        return emptyContent ?? null;
      }

      if (typeof children === "function") {
        return items.map((item: any, index: number): React.ReactNode => {
          const itemKey = item?.id ?? String(item) ?? String(index);
          const isSelected = selectedKeysSet.has(itemKey);
          const isDisabled = isItemDisabled(item);

          const itemElement = children(item);

          if (React.isValidElement(itemElement)) {
            return React.cloneElement(itemElement as React.ReactElement<any>, {
              key: itemKey,
              onClick: (e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isDisabled) {
                  handleItemClick(itemKey);
                  // Also call onChange if provided
                  if (onChange) {
                    onChange(item);
                  }
                  // Close dropdown after selection
                  if (context && selectionMode === "single") {
                    context.setIsOpen(false);
                  }
                }
                (
                  itemElement.props as {
                    onClick?: (e: React.MouseEvent) => void;
                  }
                )?.onClick?.(e);
              },
              isSelected,
              isDisabled,
              value: itemKey,
              textValue: itemKey,
              className: cn(
                itemClasses?.base,
                (itemElement.props as { className?: string })?.className
              ),
            });
          }
          return itemElement;
        });
      }

      return children;
    };

    // If using value/onChange pattern (without items), render children directly
    if (!items && (value !== undefined || onChange)) {
      const handleChange = (e: any): void => {
        onChange?.(e);
        // Close dropdown after selection
        if (context) {
          context.setIsOpen(false);
        }
      };

      return (
        <ShadcnListbox
          ref={ref}
          className={cn(
            variantClasses[variant],
            classNames?.base,
            classNames?.list,
            className
          )}
          onChange={handleChange}
          {...props}
        >
          {typeof children === "function" ? null : children}
        </ShadcnListbox>
      );
    }

    return (
      <ShadcnListbox
        ref={ref}
        className={cn(
          variantClasses[variant],
          classNames?.base,
          classNames?.list,
          className,
          disabled && "pointer-events-none opacity-50"
        )}
        {...props}
      >
        {renderItems()}
      </ShadcnListbox>
    );
  }
);
Listbox.displayName = "Listbox";

// ListboxDropdownProvider for managing dropdown state
export interface ListboxDropdownProviderProps {
  children: React.ReactNode;
}

const ListboxDropdownProvider: React.FC<ListboxDropdownProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const value = React.useMemo(
    () => ({
      isOpen,
      setIsOpen,
      dropdownRef,
    }),
    [isOpen]
  );

  return (
    <ListboxDropdownContext.Provider value={value}>
      {children}
    </ListboxDropdownContext.Provider>
  );
};

// Additional Hero UI compatible components
export interface ListboxButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  className?: string;
  autoToggle?: boolean; // If true, automatically toggle dropdown on click
}

const ListboxButton = React.forwardRef<HTMLButtonElement, ListboxButtonProps>(
  ({ className, children, autoToggle = false, onClick, ...props }, ref) => {
    const context = React.useContext(ListboxDropdownContext);

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (autoToggle && context) {
          context.setIsOpen(!context.isOpen);
        }
        onClick?.(e);
      },
      [autoToggle, context, onClick]
    );

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          "relative flex justify-between items-center w-full rounded-xl bg-background-light py-3 px-4 text-left gap-2 border-1.5",
          className
        )}
        onClick={handleClick}
        {...props}
      >
        {children}
      </button>
    );
  }
);
ListboxButton.displayName = "ListboxButton";

export interface ListboxOptionsProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  static?: boolean;
}

const ListboxOptions = React.forwardRef<HTMLDivElement, ListboxOptionsProps>(
  ({ className, children, onClick, ...props }, ref) => {
    const context = React.useContext(ListboxDropdownContext);
    const internalRef = React.useRef<HTMLDivElement>(null);
    const resolvedRef = (ref ?? internalRef) as React.RefObject<HTMLDivElement>;

    // Use context's ref if available, otherwise use the provided ref
    const finalRef = context?.dropdownRef ?? resolvedRef;

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        // Close dropdown when clicking on an option (if not static)
        if (!props.static && context) {
          // Don't close immediately, let the onChange handler do it
          // This prevents closing before the selection is made
        }
        onClick?.(e);
      },
      [context, onClick, props.static]
    );

    // Don't render if context exists and dropdown is closed
    if (context && !context.isOpen) {
      return null;
    }

    return (
      <div
        ref={finalRef}
        className={cn(
          "absolute z-40 w-full mt-1 rounded-xl bg-background-light p-2 text-base shadow-common focus:outline-none border-1.5",
          className
        )}
        onClick={handleClick}
        {...props}
      >
        {children}
      </div>
    );
  }
);
ListboxOptions.displayName = "ListboxOptions";

// ListboxOption is an alias for ListboxItem
export const ListboxOption = ListboxItem;

export {
  Listbox,
  ListboxItem,
  ListboxButton,
  ListboxOptions,
  ListboxDropdownProvider,
  useListboxDropdown,
};
